const IS_DEV = process.env.APP_VARIANT === 'development';

export const appInfo = {
  version: '3.17.1',
  hotfixVersion: '',
  androidVersionCode: 136,
  androidPackageName: IS_DEV
    ? 'com.eloveit.TaskTag.dev'
    : 'com.eloveit.TaskTag',
  iosBuildNumber: '136',
  iosBundleID: IS_DEV ? 'com.eloveit.TaskTag.dev' : 'com.eloveit.TaskTag',
};

export default ({ config }) => {
  config.name = IS_DEV ? `${config.name} (Dev)` : 'TaskTag';
  config.slug = 'TaskTag';
  config.scheme = 'com.eloveit.tasktag';
  config.version = appInfo.version + appInfo.hotfixVersion;
  config.orientation = 'portrait';
  config.icon = './assets/appIcon.png';
  config.userInterfaceStyle = 'light';
  config.owner = 'eloveitdeveloper';

  config.splash = {
    image: './assets/splash.png',
    resizeMode: 'contain',
    backgroundColor: '#000000',
  };

  config.extra = {
    eas: {
      projectId: '92b1f575-ddb1-40a4-94fc-42c64d2db95d',
    },
  };

  config.runtimeVersion = { policy: 'fingerprint' };

  config.updates = {
    fallbackToCacheTimeout: 0,
    url: 'https://u.expo.dev/92b1f575-ddb1-40a4-94fc-42c64d2db95d',
  };

  config.plugins = [
    './plugins/withAsyncStorageMavenRepo',
    './plugins/withAndroidBuildConfig',
    './plugins/withAndroidMainActivityConfigChanges',
    [
      'expo-build-properties',
      {
        android: {
          compileSdkVersion: 36,
          targetSdkVersion: 36,
          minSdkVersion: 24,
          buildToolsVersion: '36.0.0',
        },
        ios: {
          useFrameworks: 'static',
          deploymentTarget: '15.1',
          bitcode: false,
          forceStaticLinking: [
            'RNFBApp',
            'RNFBMessaging',
            'RNFBRemoteConfig',
            'RNFBCrashlytics',
            'RNFBPerf',
          ],
        },
      },
    ],

    [
      'react-native-edge-to-edge',
      {
        android: {
          parentTheme: 'Light',
          enforceNavigationBarContrast: false,
        },
      },
    ],

    '@react-native-firebase/app',
    '@react-native-firebase/perf',
    '@react-native-firebase/crashlytics',

    [
      'react-native-maps',
      {
        iosGoogleMapsApiKey: process.env.GOOGLE_PLACES_API_KEY,
        androidGoogleMapsApiKey: process.env.GOOGLE_PLACES_API_KEY,
      },
    ],

    [
      'expo-camera',
      {
        cameraPermission:
          '$(PRODUCT_NAME) lets you do things like take and share photos, record videos and use other special features.',
        microphonePermission:
          '$(PRODUCT_NAME) lets you do things like take and share photos, record videos and use other special features.',
      },
    ],

    [
      'expo-image-picker',
      {
        photosPermission:
          '$(PRODUCT_NAME) lets you do things like take and share photos, record videos and use other special features.',
        cameraPermission:
          '$(PRODUCT_NAME) lets you do things like take and share photos, record videos and use other special features.',
      },
    ],

    [
      'expo-media-library',
      {
        photosPermission:
          '$(PRODUCT_NAME) lets you do things like take and share photos, record videos and use other special features.',
        savePhotosPermission:
          '$(PRODUCT_NAME) lets you do things like take and share photos, record videos and use other special features.',
        isAccessMediaLocationEnabled: false,
        granularPermissions: [],
      },
    ],

    [
      'expo-contacts',
      {
        contactsPermission:
          '$(PRODUCT_NAME) use your contacts to find users you know and help you to collaborate.',
      },
    ],

    [
      'react-native-fbsdk-next',
      {
        appID: '242455834833440',
        clientToken: '8c2a84a68e5de533dd85e8d97d7e4d59',
        displayName: 'TaskTag',
        scheme: 'fb242455834833440',
        advertiserIDCollectionEnabled: true,
        autoLogAppEventsEnabled: true,
        isAutoInitEnabled: true,
      },
    ],

    [
      'expo-share-intent',
      {
        intentFilterAction: 'SEND',
        iosActivationRules: {
          NSExtensionActivationSupportsFileWithMaxCount: 8,
          NSExtensionActivationSupportsWebURLWithMaxCount: 1,
          NSExtensionActivationSupportsWebPageWithMaxCount: 1,
          NSExtensionActivationSupportsImageWithMaxCount: 8,
          NSExtensionActivationSupportsMovieWithMaxCount: 8,
          NSExtensionActivationSupportsText: true,
        },
        androidIntentFilters: ['text/*', 'image/*', 'video/*', '*/*'],
        androidMultiIntentFilters: ['text/*', 'image/*', 'video/*', '*/*'],
      },
    ],

    'expo-apple-authentication',

    [
      '@sentry/react-native/expo',
      {
        url: 'https://sentry.io/',
        note: 'Use SENTRY_AUTH_TOKEN env to authenticate with Sentry.',
        project: 'frontend',
        organization: 'tasktag',
      },
    ],

    'expo-asset',
    'expo-font',
    'expo-secure-store',

    [
      'expo-document-picker',
      {
        iCloudContainerEnvironment: 'Production',
      },
    ],

    [
      '@intercom/intercom-react-native',
      {
        intercomRegion: 'US',
        useManualInit: true,
      },
    ],
  ];

  config.ios = {
    ...config.ios,
    entitlements: {
      'aps-environment': IS_DEV ? 'development' : 'production',
    },
    usesAppleSignIn: true,
    bundleIdentifier: appInfo.iosBundleID,
    // Keychain sharing is required to fix the getRegistrationInfoAsync error.
    // See: https://github.com/expo/expo/issues/xxxxx (replace with relevant issue if available)
    keychainSharing: [appInfo.iosBundleID],
    buildNumber: appInfo.iosBuildNumber,
    config: {
      ...config.ios?.config,
      googleMapsApiKey: process.env.GOOGLE_PLACES_API_KEY,
      usesNonExemptEncryption: false,
    },
    supportsTablet: true,
    associatedDomains: [
      'applinks:app.tasktag.com',
      'applinks:beta.tasktag.com',
    ],
    infoPlist: {
      NSPhotoLibraryUsageDescription:
        '$(PRODUCT_NAME) lets you do things like take and share photos, record videos and use other special features.',
      NSPhotoLibraryAddUsageDescription:
        '$(PRODUCT_NAME) lets you do things like take and share photos, record videos and use other special features.',
      NSMicrophoneUsageDescription:
        '$(PRODUCT_NAME) require microphone permission to capture and record your voice and upload as recording.',
      NSLocationWhenInUseUsageDescription:
        '$(PRODUCT_NAME) needs access to your location to provide location-based features, such as nearby results or check-ins.',
      UIBackgroundModes: ['remote-notification', 'fetch'],
    },
    googleServicesFile: IS_DEV
      ? './GoogleService-Info.plist'
      : './GoogleService-Info_production.plist',
  };

  config.android = {
    ...config.android,
    package: appInfo.androidPackageName,
    versionCode: appInfo.androidVersionCode,
    permissions: [
      'android.permission.RECORD_AUDIO',
      'android.permission.CAMERA',
      'android.permission.ACCESS_FINE_LOCATION',
      'android.permission.ACCESS_COARSE_LOCATION',
    ],
    blockedPermissions: [
      'android.permission.READ_MEDIA_IMAGES',
      'android.permission.READ_MEDIA_VIDEO',
      'android.permission.READ_MEDIA_VISUAL_USER_SELECTED',
      'android.permission.READ_EXTERNAL_STORAGE',
      'android.permission.WRITE_EXTERNAL_STORAGE',
      'android.permission.ACCESS_MEDIA_LOCATION',
    ],
    adaptiveIcon: {
      foregroundImage: './assets/adaptive-icon.png',
      backgroundColor: '#000000',
    },
    icon: './assets/appIcon.png',
    googleServicesFile: './google-services.json',
    intentFilters: [
      {
        action: 'VIEW',
        autoVerify: true,
        data: [
          { scheme: 'https', host: 'beta.tasktag.com' },
          { scheme: 'https', host: 'app.tasktag.com' },
        ],
        category: ['BROWSABLE', 'DEFAULT'],
      },
    ],
    config: {
      ...config.android?.config,
      googleMaps: {
        apiKey: process.env.GOOGLE_PLACES_API_KEY,
      },
    },
  };

  config.web = {
    favicon: './assets/favicon.png',
  };

  return { ...config };
};
